import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Clapperboard, Loader2, Save, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { fetchVideoGenerationPrompt } from '@/lib/videoGenerationPrompt';
import { VideoGenerationPromptDialog } from '@/components/VideoGenerationPromptDialog';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

function mediaUrlForPlayback(url) {
  if (!url) return url;
  if (url.startsWith('/api/')) return `${BACKEND_ORIGIN}${url}`;
  return url;
}

function collectDialogueVideoVersions(scene) {
  const dsv = scene?.dialogue_scene_video;
  if (!dsv || typeof dsv !== 'object') return [];
  const mainKey = 'video_url';
  const historyKey = 'video_history';
  const byUrl = new Map();

  const ensureEntry = (raw, sourceHint = null) => {
    if (!raw) return;
    const entry = typeof raw === 'string' ? { url: raw } : raw;
    if (!entry?.url) return;
    const existing = byUrl.get(entry.url) || {};
    byUrl.set(entry.url, {
      url: entry.url,
      createdAt: entry.created_at || existing.createdAt || null,
      source: entry.source || sourceHint || existing.source || null,
    });
  };

  (dsv[historyKey] || []).forEach((item) => ensureEntry(item));
  ensureEntry(
    {
      url: dsv[mainKey],
      created_at: scene.updated_at || scene.created_at || null,
      source: 'active',
    },
    'active'
  );

  const versions = Array.from(byUrl.values()).map((entry) => ({
    ...entry,
    isActive: entry.url === dsv[mainKey],
    createdAtMs: entry.createdAt ? new Date(entry.createdAt).getTime() : 0,
  }));

  versions.sort((a, b) => b.createdAtMs - a.createdAtMs);
  return versions;
}

const CAMERA_MOTION_CHOICES = {
  nano_banana: [
    { value: 'static', label: 'Static' },
    { value: 'pan_left', label: 'Pan left' },
    { value: 'pan_right', label: 'Pan right' },
    { value: 'tilt_up', label: 'Tilt up' },
    { value: 'tilt_down', label: 'Tilt down' },
    { value: 'zoom_in', label: 'Zoom in' },
    { value: 'zoom_out', label: 'Zoom out' },
  ],
  minimax: [
    { value: 'auto', label: 'Auto' },
    { value: 'static', label: 'Static' },
    { value: 'dolly_in', label: 'Dolly in' },
    { value: 'dolly_out', label: 'Dolly out' },
    { value: 'pan_left', label: 'Pan left' },
    { value: 'pan_right', label: 'Pan right' },
  ],
  veo3: [
    { value: 'auto', label: 'Auto' },
    { value: 'static', label: 'Static' },
    { value: 'dolly_in', label: 'Dolly in' },
    { value: 'dolly_out', label: 'Dolly out' },
    { value: 'pan_left', label: 'Pan left' },
    { value: 'pan_right', label: 'Pan right' },
    { value: 'tilt_up', label: 'Tilt up' },
    { value: 'tilt_down', label: 'Tilt down' },
  ],
  runway: [
    { value: 'auto', label: 'Auto' },
    { value: 'static', label: 'Static' },
    { value: 'dolly_in', label: 'Dolly in' },
    { value: 'dolly_out', label: 'Dolly out' },
    { value: 'pan_left', label: 'Pan left' },
    { value: 'pan_right', label: 'Pan right' },
    { value: 'tilt_up', label: 'Tilt up' },
    { value: 'tilt_down', label: 'Tilt down' },
  ],
  seedance: [
    { value: 'auto', label: 'Auto' },
    { value: 'static', label: 'Static' },
    { value: 'dolly_in', label: 'Dolly in' },
    { value: 'dolly_out', label: 'Dolly out' },
    { value: 'pan_left', label: 'Pan left' },
    { value: 'pan_right', label: 'Pan right' },
    { value: 'tilt_up', label: 'Tilt up' },
    { value: 'tilt_down', label: 'Tilt down' },
  ],
  seedance_2_5: [
    { value: 'auto', label: 'Auto' },
    { value: 'static', label: 'Static' },
    { value: 'dolly_in', label: 'Dolly in' },
    { value: 'dolly_out', label: 'Dolly out' },
    { value: 'pan_left', label: 'Pan left' },
    { value: 'pan_right', label: 'Pan right' },
    { value: 'tilt_up', label: 'Tilt up' },
    { value: 'tilt_down', label: 'Tilt down' },
  ],
  chatgpt_astra: [
    { value: 'auto', label: 'Auto' },
    { value: 'static', label: 'Static' },
    { value: 'dolly_in', label: 'Dolly in' },
    { value: 'dolly_out', label: 'Dolly out' },
    { value: 'pan_left', label: 'Pan left' },
    { value: 'pan_right', label: 'Pan right' },
    { value: 'tilt_up', label: 'Tilt up' },
    { value: 'tilt_down', label: 'Tilt down' },
  ],
  fable_5_1: [
    { value: 'auto', label: 'Auto' },
    { value: 'static', label: 'Static' },
    { value: 'dolly_in', label: 'Dolly in' },
    { value: 'dolly_out', label: 'Dolly out' },
    { value: 'pan_left', label: 'Pan left' },
    { value: 'pan_right', label: 'Pan right' },
    { value: 'tilt_up', label: 'Tilt up' },
    { value: 'tilt_down', label: 'Tilt down' },
  ],
  inhouse: [
    { value: 'auto', label: 'Auto' },
    { value: 'static', label: 'Static' },
    { value: 'dolly_in', label: 'Dolly in' },
    { value: 'dolly_out', label: 'Dolly out' },
    { value: 'pan_left', label: 'Pan left' },
    { value: 'pan_right', label: 'Pan right' },
    { value: 'tilt_up', label: 'Tilt up' },
    { value: 'tilt_down', label: 'Tilt down' },
  ],
  ltx: [
    { value: 'auto', label: 'Auto' },
    { value: 'static', label: 'Static' },
  ],
};

const ENGINES = [
  { value: 'runway', label: 'Runway' },
  { value: 'seedance', label: 'Seedance 2' },
  { value: 'seedance_2_5', label: 'Seedance 2.5' },
  { value: 'chatgpt_astra', label: 'ChatGPT Astra' },
  { value: 'fable_5_1', label: 'Fable 5.1' },
  { value: 'minimax', label: 'MiniMax' },
  { value: 'veo3', label: 'Veo 3' },
  { value: 'ltx', label: 'LTX' },
  { value: 'nano_banana', label: 'Nano Banana' },
  { value: 'inhouse', label: 'Inhouse' },
];

export function DialogueSceneVideoBoardPage() {
  const { storyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sceneId = searchParams.get('scene') || '';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [boardPayload, setBoardPayload] = useState(null);
  const [generationConfig, setGenerationConfig] = useState({
    extra_prompt: '',
    prompt_priority: 'blueprint_scene_then_script',
    provider: 'runway',
    camera_motion: 'auto',
  });
  const [oneShotPrompt, setOneShotPrompt] = useState('');
  const [videoPromptReviewOpen, setVideoPromptReviewOpen] = useState(false);
  const [videoPromptReviewLoading, setVideoPromptReviewLoading] = useState(false);
  const [videoPromptDraft, setVideoPromptDraft] = useState('');
  const [videoPromptMaxLength, setVideoPromptMaxLength] = useState(2000);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [preparing, setPreparing] = useState(false);

  const scene = boardPayload?.scene;
  const cameraChoices =
    CAMERA_MOTION_CHOICES[generationConfig.provider] || CAMERA_MOTION_CHOICES.runway;

  const videoVersions = useMemo(() => collectDialogueVideoVersions(scene || {}), [scene]);

  const loadBoard = useCallback(async () => {
    if (!storyId) return;
    setLoading(true);
    try {
      const params = {};
      if (sceneId) params.scene_id = sceneId;
      const { data } = await api.get(`/stories/${storyId}/dialogue-scene-board`, { params });
      setBoardPayload(data);
      if (!sceneId && data?.selected_scene_id) {
        setSearchParams({ scene: data.selected_scene_id }, { replace: true });
      }
      const gc = data.generation_config || {};
      setGenerationConfig((prev) => ({
        extra_prompt: gc.extra_prompt ?? prev.extra_prompt ?? '',
        prompt_priority: gc.prompt_priority ?? prev.prompt_priority ?? 'blueprint_scene_then_script',
        provider: gc.provider ?? data.default_scene_video_provider ?? prev.provider ?? 'runway',
        camera_motion: gc.camera_motion ?? prev.camera_motion ?? 'auto',
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load dialogue scene board');
      setBoardPayload(null);
    } finally {
      setLoading(false);
    }
  }, [storyId, sceneId, setSearchParams]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const pollJob = (jobId, onDone) => {
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        if (data.status === 'completed') {
          clearInterval(interval);
          onDone(null);
        } else if (data.status === 'failed') {
          clearInterval(interval);
          onDone(data.error_message || 'Generation failed');
        }
      } catch (_) {}
    }, 2500);
    return interval;
  };

  const saveConfig = async (refreshSnapshot = false) => {
    setSaving(true);
    try {
      await api.patch(`/scenes/${sceneId}/dialogue-scene-board`, {
        generation_config: generationConfig,
        refresh_board_snapshot: refreshSnapshot,
      });
      toast.success(refreshSnapshot ? 'Configuration & board snapshot saved' : 'Configuration saved');
      await loadBoard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const executeGenerate = async (editedPrompt) => {
    setGenerating(true);
    try {
      const params = {
        provider: generationConfig.provider,
      };
      if (generationConfig.camera_motion && generationConfig.camera_motion !== 'auto') {
        params.camera_motion = generationConfig.camera_motion;
      }
      const body = {
        refresh_board_snapshot: true,
        prompt: (editedPrompt || '').trim(),
      };
      const { data } = await api.post(
        `/scenes/${sceneId}/dialogue-scene-board/generate-video`,
        body,
        { params },
      );
      toast.info('Dialogue clip generation started…');
      pollJob(data.job_id, (err) => {
        setGenerating(false);
        if (err) toast.error(err);
        else {
          toast.success('Dialogue scene video ready');
          loadBoard();
        }
      });
    } catch (err) {
      setGenerating(false);
      toast.error(err.response?.data?.detail || 'Could not start generation');
    }
  };

  const runGenerate = async () => {
    setVideoPromptReviewOpen(true);
    setVideoPromptReviewLoading(true);
    try {
      const data = await fetchVideoGenerationPrompt({
        sceneId,
        provider: generationConfig.provider,
        cameraMotion: generationConfig.camera_motion,
      });
      const composed = data.prompt || oneShotPrompt.trim();
      setVideoPromptDraft(composed);
      setVideoPromptMaxLength(data.max_length || 2000);
      setOneShotPrompt(composed);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load video prompt');
      setVideoPromptReviewOpen(false);
    } finally {
      setVideoPromptReviewLoading(false);
    }
  };

  const selectVersion = async (url) => {
    try {
      await api.post(`/scenes/${sceneId}/select-media`, {
        kind: 'video',
        url,
        video_track: 'dialogue',
      });
      toast.success('Active dialogue clip updated');
      setHistoryOpen(false);
      loadBoard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not switch version');
    }
  };

  const prepareAllScenes = async (forceRegenerate = false) => {
    setPreparing(true);
    try {
      const { data } = await api.post(`/stories/${storyId}/dialogue-scene-board/prepare`, null, {
        params: { force_regenerate_backgrounds: forceRegenerate },
      });
      toast.success('Scene references prepared');
      const first = data?.first_scene_id || sceneId;
      if (first) setSearchParams({ scene: first }, { replace: true });
      await loadBoard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to prepare scene references');
    } finally {
      setPreparing(false);
    }
  };

  const snap = boardPayload?.board_snapshot;

  return (
    <div className="min-h-screen theme-page-bg pb-16">
      <div className="border-b" style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-lg"
              onClick={() => navigate(`/stories/${storyId}/edit`)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Scene grid
            </Button>
            <Clapperboard className="w-5 h-5 shrink-0" style={{ color: 'var(--primary)' }} />
            <div className="min-w-0">
              <h1 className="text-lg font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                Dialogue scene board
              </h1>
              <p className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>
                {boardPayload?.story_title || '…'} · Scene {snap?.scene_number ?? '…'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-lg"
              onClick={() => prepareAllScenes(false)}
              disabled={preparing}
            >
              {preparing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Prepare scene refs
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-lg"
              onClick={() => prepareAllScenes(true)}
              disabled={preparing}
            >
              Refresh backgrounds
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {loading ? (
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <Loader2 className="w-5 h-5 animate-spin" /> Loading board…
          </div>
        ) : (
          <>
            <div
              className="glass-panel rounded-2xl p-5 space-y-4"
              style={{ border: '1px solid var(--glass-border)' }}
            >
              {Array.isArray(boardPayload?.scenes) && boardPayload.scenes.length > 0 && (
                <label className="text-xs space-y-1 block">
                  <span style={{ color: 'var(--text-tertiary)' }}>Scene</span>
                  <select
                    className="w-full rounded-lg border px-2 py-2 text-sm"
                    style={{
                      background: 'var(--bg-secondary)',
                      borderColor: 'var(--glass-border)',
                      color: 'var(--text-primary)',
                    }}
                    value={scene?.id || ''}
                    onChange={(e) => setSearchParams({ scene: e.target.value })}
                  >
                    {boardPayload.scenes.map((s) => (
                      <option key={s.id} value={s.id}>
                        Scene {s.scene_number}: {s.scene_title || 'Untitled'}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Blueprint &amp; script snapshot
              </h2>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[10px] uppercase tracking-wide mb-1" style={{ color: 'var(--text-tertiary)' }}>
                    Match
                  </p>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    {snap?.blueprint_matched ? 'Blueprint scene aligned by scene order' : 'No blueprint segment matched — script-only'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide mb-1" style={{ color: 'var(--text-tertiary)' }}>
                    Cast
                  </p>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    {(snap?.cast_names || []).join(', ') || '—'}
                  </p>
                </div>
              </div>
              {snap?.blueprint_video_prompt_excerpt ? (
                <div>
                  <p className="text-[10px] uppercase tracking-wide mb-1" style={{ color: 'var(--text-tertiary)' }}>
                    Blueprint video prompt (excerpt)
                  </p>
                  <p className="text-sm whitespace-pre-wrap max-h-40 overflow-y-auto leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    {snap.blueprint_video_prompt_excerpt}
                  </p>
                </div>
              ) : null}
              <div>
                <p className="text-[10px] uppercase tracking-wide mb-1" style={{ color: 'var(--text-tertiary)' }}>
                  Dialogue block (blueprint or audio script)
                </p>
                <p className="text-sm whitespace-pre-wrap max-h-48 overflow-y-auto leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                  {snap?.primary_dialogue_block || '—'}
                </p>
              </div>
              {scene?.dialogue_scene_video?.image_preparation && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide mb-1" style={{ color: 'var(--text-tertiary)' }}>
                    Prepared scene references
                  </p>
                  <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Segment: {scene.dialogue_scene_video.image_preparation.segment_key} · Camera hint:{' '}
                    {scene.dialogue_scene_video.image_preparation.camera_angle_hint}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(scene.dialogue_scene_video.image_preparation.scene_reference_images || []).map((u) => (
                      <a
                        key={u}
                        href={mediaUrlForPlayback(u)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[10px] underline"
                        style={{ color: 'var(--primary)' }}
                      >
                        reference
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              className="glass-panel rounded-2xl p-5 space-y-4"
              style={{ border: '1px solid var(--glass-border)' }}
            >
              <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Generation parameters
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-xs space-y-1 block">
                  <span style={{ color: 'var(--text-tertiary)' }}>Model</span>
                  <select
                    className="w-full rounded-lg border px-2 py-2 text-sm"
                    style={{
                      background: 'var(--bg-secondary)',
                      borderColor: 'var(--glass-border)',
                      color: 'var(--text-primary)',
                    }}
                    value={generationConfig.provider}
                    onChange={(e) =>
                      setGenerationConfig((c) => ({ ...c, provider: e.target.value, camera_motion: 'auto' }))
                    }
                  >
                    {ENGINES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs space-y-1 block">
                  <span style={{ color: 'var(--text-tertiary)' }}>Camera motion</span>
                  <select
                    className="w-full rounded-lg border px-2 py-2 text-sm"
                    style={{
                      background: 'var(--bg-secondary)',
                      borderColor: 'var(--glass-border)',
                      color: 'var(--text-primary)',
                    }}
                    value={generationConfig.camera_motion}
                    onChange={(e) =>
                      setGenerationConfig((c) => ({ ...c, camera_motion: e.target.value }))
                    }
                  >
                    {cameraChoices.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs space-y-1 block sm:col-span-2">
                  <span style={{ color: 'var(--text-tertiary)' }}>Prompt merge order</span>
                  <select
                    className="w-full rounded-lg border px-2 py-2 text-sm"
                    style={{
                      background: 'var(--bg-secondary)',
                      borderColor: 'var(--glass-border)',
                      color: 'var(--text-primary)',
                    }}
                    value={generationConfig.prompt_priority}
                    onChange={(e) =>
                      setGenerationConfig((c) => ({ ...c, prompt_priority: e.target.value }))
                    }
                  >
                    <option value="blueprint_scene_then_script">Blueprint → scene → dialogue → extra</option>
                    <option value="script_then_blueprint">Dialogue → blueprint → scene → extra</option>
                  </select>
                </label>
              </div>
              <label className="text-xs space-y-1 block">
                <span style={{ color: 'var(--text-tertiary)' }}>Extra prompt (saved with scene)</span>
                <Textarea
                  rows={3}
                  value={generationConfig.extra_prompt}
                  onChange={(e) =>
                    setGenerationConfig((c) => ({ ...c, extra_prompt: e.target.value }))
                  }
                  className="rounded-xl text-sm"
                  style={{
                    background: 'var(--bg-secondary)',
                    borderColor: 'var(--glass-border)',
                    color: 'var(--text-primary)',
                  }}
                  placeholder="Additional cinematic cues, shot preferences, safety…"
                />
              </label>
              <label className="text-xs space-y-1 block">
                <span style={{ color: 'var(--text-tertiary)' }}>One-shot prompt (this run only)</span>
                <Textarea
                  rows={2}
                  value={oneShotPrompt}
                  onChange={(e) => setOneShotPrompt(e.target.value)}
                  className="rounded-xl text-sm"
                  style={{
                    background: 'var(--bg-secondary)',
                    borderColor: 'var(--glass-border)',
                    color: 'var(--text-primary)',
                  }}
                  placeholder="Appended only for the next generation request"
                />
              </label>

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  className="rounded-lg"
                  style={{ background: 'var(--primary)', color: 'white' }}
                  disabled={saving}
                  onClick={() => saveConfig(false)}
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save parameters
                </Button>
                <Button type="button" variant="outline" className="rounded-lg" disabled={saving} onClick={() => saveConfig(true)}>
                  Save &amp; refresh board snapshot
                </Button>
                <Button
                  type="button"
                  className="rounded-lg"
                  style={{ background: '#f97316', color: 'white' }}
                  disabled={generating}
                  onClick={runGenerate}
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <TwinneeLogo className="w-4 h-4 mr-2" />
                  )}
                  Generate dialogue clip
                </Button>
              </div>
            </div>

            <div
              className="glass-panel rounded-2xl p-5 space-y-3"
              style={{ border: '1px solid var(--glass-border)' }}
            >
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <Video className="w-4 h-4" /> Dialogue track preview
                </h2>
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setHistoryOpen(true)}>
                  Versions ({videoVersions.length})
                </Button>
              </div>
              <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                Stored separately from the grid &quot;main&quot; video: persists as <code className="text-[10px]">dialogue_scene_video</code> on the scene and uploads to S3 as <code className="text-[10px]">/api/media/…</code>.
              </p>
              {scene?.dialogue_scene_video?.video_url ? (
                <video
                  key={scene.dialogue_scene_video.video_url}
                  controls
                  className="w-full rounded-xl aspect-video bg-black/80 max-h-[min(70vh,520px)]"
                  src={mediaUrlForPlayback(scene.dialogue_scene_video.video_url)}
                />
              ) : (
                <div
                  className="aspect-video rounded-xl flex items-center justify-center text-sm"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' }}
                >
                  No dialogue clip yet — generate above.
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent
          className="max-w-lg rounded-2xl max-h-[80vh] overflow-y-auto"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
        >
          <DialogHeader>
            <DialogTitle className="text-base" style={{ color: 'var(--text-primary)' }}>
              Dialogue clip versions
            </DialogTitle>
          </DialogHeader>
          <ul className="space-y-2 text-sm">
            {videoVersions.map((v) => (
              <li
                key={v.url}
                className="flex items-center justify-between gap-2 p-2 rounded-lg"
                style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}
              >
                <div className="min-w-0">
                  <p className="truncate text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                    {v.url}
                  </p>
                  <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                    {v.source || ''} · {v.createdAt || ''}
                    {v.isActive ? ' · active' : ''}
                  </p>
                </div>
                {!v.isActive && (
                  <Button size="sm" variant="outline" className="shrink-0 rounded-lg" onClick={() => selectVersion(v.url)}>
                    Use
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>

      <VideoGenerationPromptDialog
        open={videoPromptReviewOpen}
        onOpenChange={setVideoPromptReviewOpen}
        provider={generationConfig.provider}
        initialPrompt={videoPromptDraft}
        maxLength={videoPromptMaxLength}
        loading={videoPromptReviewLoading}
        title="Review dialogue video prompt"
        onConfirm={(edited) => {
          setVideoPromptReviewOpen(false);
          setOneShotPrompt(edited);
          executeGenerate(edited);
        }}
      />
    </div>
  );
}
